import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    ElementRef,
    NgZone,
    Renderer2,
    inject,
    input,
    output,
    signal,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { IconComponent } from '@interacta-shared/ui-icons';
import { KeyCode } from '@interacta-shared/util';
import { TranslateModule } from '@ngx-translate/core';
import { filter, fromEvent } from 'rxjs';

@Component({
    selector: 'interacta-horizontal-panel-resizer',
    templateUrl: './horizontal-panel-resizer.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [IconComponent, TranslateModule],
})
export class HorizontalPanelResizerComponent {
    private readonly zone = inject(NgZone);
    private readonly renderer = inject(Renderer2);
    private readonly destroyRef = inject(DestroyRef);

    /**
     * HTML element of the panel to resize
     */
    readonly panel = input.required<HTMLElement>();

    /**
     * Side where the panel is located
     */
    readonly side = input<'left' | 'right'>('left');

    /**
     * Minimum width of the panel in pixels
     */
    readonly minWidth = input<number>(200);

    /**
     * Maximum width of the panel in pixels (optional)
     */
    readonly maxWidth = input<number | null>(null);

    /**
     * Step size for keyboard navigation in pixels
     */
    readonly keyboardStep = input<number>(20);

    /**
     * Emits the new width when the resize is completed
     */
    readonly widthChanged = output<number>();

    /**
     * Emits true when resizing is in progress, false when it ends
     */
    readonly resizing = output<boolean>();

    readonly resizerHandle =
        viewChild.required<ElementRef<HTMLElement>>('resizerHandle');

    readonly currentWidth = signal<number>(0);
    readonly isResizing = signal<boolean>(false);

    private startX: number | null = null;
    private startWidth: number | null = null;

    constructor() {
        toObservable(this.panel)
            .pipe(
                filter((el) => !!el),
                takeUntilDestroyed(),
            )
            .subscribe((el) => {
                this.currentWidth.set(el.offsetWidth);
            });

        toObservable(this.resizerHandle)
            .pipe(
                filter((ref) => !!ref),
                takeUntilDestroyed(),
            )
            .subscribe((ref) => {
                this.setupMouseListeners(ref.nativeElement);
            });
    }

    private setupMouseListeners(element: HTMLElement): void {
        this.zone.runOutsideAngular(() => {
            fromEvent<MouseEvent>(element, 'mousedown')
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((event) => this.onMouseDown(event));

            fromEvent<MouseEvent>(document, 'mousemove')
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((event) => this.onMouseMove(event));

            fromEvent<MouseEvent>(document, 'mouseup')
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(() => this.onMouseUp());
        });
    }

    onKeyDown(event: KeyboardEvent): void {
        const step = this.keyboardStep();
        const current = this.currentWidth();
        const max = this.maxWidth();

        let delta = 0;
        let handled = false;

        switch (event.key) {
            case KeyCode.LEFT_ARROW:
                delta = this.side() === 'left' ? -step : step;
                handled = true;
                break;
            case KeyCode.RIGHT_ARROW:
                delta = this.side() === 'left' ? step : -step;
                handled = true;
                break;
            case KeyCode.HOME:
                this.updateWidth(this.minWidth());
                handled = true;
                break;
            case KeyCode.END:
                if (max !== null) {
                    this.updateWidth(max);
                    handled = true;
                }
                break;
        }

        if (handled) {
            event.preventDefault();
            event.stopPropagation();

            if (delta !== 0) {
                this.updateWidth(current + delta);
            }
        }
    }

    private onMouseDown(event: MouseEvent): void {
        this.widthChanged.emit(this.panel().offsetWidth);

        this.startX = event.pageX;
        this.startWidth = this.panel().offsetWidth;

        this.currentWidth.set(this.startWidth);
        this.isResizing.set(true);

        this.zone.run(() => this.resizing.emit(true));
    }

    private onMouseMove(event: MouseEvent): void {
        if (
            this.startWidth === null ||
            this.startX === null ||
            !this.isResizing()
        ) {
            return;
        }

        event.preventDefault();

        const delta = this.calculateDelta(event.pageX);
        const newWidth = this.startWidth + delta;

        this.applyWidthStyle(newWidth);
    }

    private onMouseUp(): void {
        if (!this.startX && !this.startWidth) return;

        this.zone.run(() => {
            if (this.isResizing()) {
                const finalWidth = this.panel().offsetWidth;
                this.currentWidth.set(finalWidth);
                this.widthChanged.emit(finalWidth);
                this.resizing.emit(false);
            }
            this.isResizing.set(false);
        });

        this.startWidth = null;
        this.startX = null;
    }

    private updateWidth(width: number): void {
        const constrained = this.constrainWidth(width);
        this.applyWidthStyle(constrained);
        this.currentWidth.set(constrained);
        this.widthChanged.emit(constrained);
    }

    private applyWidthStyle(width: number): void {
        const constrained = this.constrainWidth(width);
        this.renderer.setStyle(this.panel(), 'width', `${constrained}px`);
    }

    private constrainWidth(width: number): number {
        const min = this.minWidth();
        const max = this.maxWidth();
        let w = Math.max(width, min);
        if (max !== null) w = Math.min(w, max);
        return w;
    }

    private calculateDelta(currentX: number): number {
        const startX = this.startX ?? 0;
        const side = this.side();
        return side === 'left' ? currentX - startX : startX - currentX;
    }
}
